import { TonConnectButton, useTonAddress } from '@tonconnect/ui-react';
import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TON_NETWORK } from '../ton/config';
import { NavigationV2, type TabV2 } from './NavigationV2';
import { PyramidSpinner } from './PyramidSpinner';
import { AddToHomeScreen } from './AddToHomeScreen';
import { LanguageSwitcher } from './LanguageSwitcher';
import { LegalLinks } from './LegalLinks';
import { PartnerDashboardLink } from './PartnerDashboardLink';

interface SidebarV2Props {
  active: TabV2;
  renderWalletControl: boolean;
  onChange: (tab: TabV2) => void;
  onOpenProfile: () => void;
  onGoHome: () => void;
}

/** Desktop-only vertical rail: animated brand, three anchors, wallet chip
 *  and the utility links that live in the profile sheet on mobile. */
export function SidebarV2({ active, renderWalletControl, onChange, onOpenProfile, onGoHome }: SidebarV2Props) {
  const address = useTonAddress();
  const { t } = useTranslation();
  return (
    <aside className="app-sidebar sidebar-v2">
      <button
        type="button"
        className="sidebar__brand brand-button"
        onClick={onGoHome}
        aria-label={t('v2.nav.play')}
      >
        <PyramidSpinner className="sidebar-v2__pyramid" size={34} />
        <img src="/text-logo.webp" alt="PRMD" className="sidebar__logo" />
        {TON_NETWORK !== 'mainnet' && (
          <span className={`network-badge network-badge--${TON_NETWORK}`}>{TON_NETWORK}</span>
        )}
      </button>

      <div className="sidebar__nav">
        <NavigationV2 active={active} onChange={onChange} variant="side" />
      </div>

      <div className="sidebar__footer">
        <button
          type="button"
          className="sidebar-v2__menu"
          onClick={onOpenProfile}
          aria-label={t('v2.profile.open')}
        >
          <Menu size={18} aria-hidden="true" /> {t('v2.profile.open')}
        </button>
        <LanguageSwitcher className="lang-switcher--block" />
        <AddToHomeScreen variant="block" />
        {address && renderWalletControl ? (
          <TonConnectButton />
        ) : (
          <p className="sidebar__hint">{t('sidebar.connectHint')}</p>
        )}
        <PartnerDashboardLink className="partner-link--quiet" />
        <LegalLinks className="legal-links--sidebar" />
      </div>
    </aside>
  );
}
